/**
 * Create form for an establishment.
 */
import * as React from 'react';
import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import {
  applyEstablishmentFormField,
  applyEstablishmentFormPatch,
  buildCreateEstablishmentDto,
  emptyCreateEstablishmentForm,
  formatEstablishmentPersonLabel,
  getClassesError,
  revalidateLegacyContactFields,
} from '@/components/establishments/establishment-form-utils';
import { EstablishmentGeneralSection } from '@/components/establishments/establishment-form-builder/EstablishmentGeneralSection';
import { EstablishmentAddressSection } from '@/components/establishments/establishment-form-builder/EstablishmentAddressSection';
import { EstablishmentContactsSection } from '@/components/establishments/establishment-form-builder/EstablishmentContactsSection';
import { EstablishmentEngagementSection } from '@/components/establishments/establishment-form-builder/EstablishmentEngagementSection';
import { EstablishmentProspectionSection } from '@/components/establishments/establishment-form-builder/EstablishmentProspectionSection';
import { EstablishmentPartnershipSection } from '@/components/establishments/establishment-form-builder/EstablishmentPartnershipSection';
import { EstablishmentManagersSection } from '@/components/establishments/establishment-form-builder/EstablishmentManagersSection';
import { EstablishmentClassroomsSection } from '@/components/establishments/establishment-form-builder/EstablishmentClassroomsSection';
import { EstablishmentClassesSection } from '@/components/establishments/establishment-form-builder/EstablishmentClassesSection';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { establishmentValidationSchema } from '@/utils/validation-schemas-utils';
import { createEstablishment } from '@/services/establishments.service';
import type { CreateEstablishmentDto, EstablishmentFormState } from '@/types/establishment.types';

/** Props for {@link CreateEstablishmentView}. */
interface CreateEstablishmentViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for an establishment. */
export const CreateEstablishmentView: React.FC<CreateEstablishmentViewProps> = ({
  onClose,
  onSuccess,
}) => {
  const { user, isManager } = useConnectedUser();
  const validation = useFormValidation(establishmentValidationSchema);
  const [establishmentData, setEstablishmentData] = React.useState(() =>
    emptyCreateEstablishmentForm(isManager && user ? user.id : ''),
  );
  const formDataRef = React.useRef(establishmentData);
  formDataRef.current = establishmentData;

  const {
    options: coordinatorOptions,
    isLoading: coordinatorLoading,
    onSearch: onCoordinatorSearch,
  } = useAdminAutocomplete({
    roleCodes: ['coordinator'],
    managedById: establishmentData.managedById || undefined,
    enabled: !!establishmentData.managedById,
  });

  const {
    options: managerOptions,
    isLoading: managerLoading,
    onSearch: onManagerSearch,
  } = useAdminAutocomplete({
    roleCodes: ['manager'],
  });

  const createMutation = useMutation({
    mutationFn: async (dto: CreateEstablishmentDto) => {
      return createEstablishment(dto);
    },
    onSuccess: () => {
      toast.success('Établissement créé avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleFieldChange = (field: string, value: unknown) => {
    const updatedData = applyEstablishmentFormField(formDataRef.current, field, value);
    formDataRef.current = updatedData;
    setEstablishmentData(updatedData);
    validation.validateField(field, value, updatedData);
    for (const legacyField of revalidateLegacyContactFields(field, value)) {
      validation.validateField(legacyField, updatedData[legacyField], updatedData);
    }
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleFormPatch = (patch: Partial<EstablishmentFormState>) => {
    const updatedData = applyEstablishmentFormPatch(formDataRef.current, patch);
    formDataRef.current = updatedData;
    setEstablishmentData(updatedData);
    for (const [key, value] of Object.entries(patch)) {
      validation.validateField(key, value, updatedData);
      validation.markAsTouched(key);
      validation.markAsDirty(key);
    }
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  const handleSubmit = () => {
    if (!validation.validateForm(establishmentData)) return;
    createMutation.mutate(
      buildCreateEstablishmentDto(establishmentData, { isManager, currentUserId: user?.id }),
    );
  };

  const classesError = useMemo(
    () => getClassesError(establishmentData.type, establishmentData.classes),
    [establishmentData.classes, establishmentData.type],
  );

  const fieldValidation = {
    getFieldState: validation.getFieldState,
    getFieldError: validation.getFieldError,
  };

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un établissement"
      subtitle="Renseignez les informations de l'établissement"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={validation.isFormValid() && !classesError}
    >
      <div className="space-y-6">
        <EstablishmentGeneralSection
          mode="create"
          form={establishmentData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={fieldValidation}
        />
        <EstablishmentAddressSection
          form={establishmentData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          onFormPatch={handleFormPatch}
          validation={fieldValidation}
        />
        <EstablishmentContactsSection form={establishmentData} onFieldChange={handleFieldChange} />
        <EstablishmentEngagementSection
          mode="create"
          form={establishmentData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={fieldValidation}
        />
        <EstablishmentProspectionSection
          mode="create"
          form={establishmentData}
          onFieldChange={handleFieldChange}
        />
        <EstablishmentPartnershipSection
          form={establishmentData}
          onFieldChange={handleFieldChange}
        />
        <EstablishmentManagersSection
          form={establishmentData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={fieldValidation}
          isManager={isManager}
          managerLabel={formatEstablishmentPersonLabel(user ?? {})}
          managerOptions={managerOptions}
          managerLoading={managerLoading}
          onManagerSearch={onManagerSearch}
          coordinatorOptions={coordinatorOptions}
          coordinatorLoading={coordinatorLoading}
          onCoordinatorSearch={onCoordinatorSearch}
        />
        <EstablishmentClassroomsSection
          form={establishmentData}
          onFieldChange={handleFieldChange}
        />
        <EstablishmentClassesSection
          mode="create"
          form={establishmentData}
          onFieldChange={handleFieldChange}
          classesError={classesError}
        />
      </div>
    </CollapseReplaceForm>
  );
};
